import React, { useState } from 'react';
import { doc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';

const AdminApproval = () => {
  const { currentUser, fetchUserData, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [adminEmail, setAdminEmail] = useState('admin@the-apn.com');
  const [promoteLoading, setPromoteLoading] = useState(false);

  const handleApproveAdmin = async () => {
    if (!currentUser) return;

    setLoading(true);
    setMessage('');

    try {
      // Update the current user to be an approved admin
      await updateDoc(doc(db, 'users', currentUser.uid), {
        status: 'approved',
        role: 'admin',
        membershipTier: 'admin',
        isAdmin: true,
        approvedAt: new Date(),
        approvedBy: 'self-approval',
        updatedAt: new Date()
      });

      setMessage('✅ Admin account approved successfully! Refreshing your session...');

      // Wait a moment for the database to update
      setTimeout(async () => {
        try {
          // Refresh user data
          await fetchUserData(currentUser.uid);
          setMessage('✅ Admin account approved and session refreshed! Redirecting to dashboard...');

          // Redirect to admin dashboard after a short delay
          setTimeout(() => {
            navigate('/admin');
          }, 2000);
        } catch (error) {
          console.error('Error refreshing user data:', error);
          setMessage('✅ Admin account approved! Please sign out and sign back in to see changes.');
        }
      }, 1000);

    } catch (error) {
      console.error('Error approving admin:', error);
      setMessage(`❌ Error approving admin: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const promoteUserByEmail = async () => {
    if (!adminEmail) {
      setMessage('❌ Please enter an email address');
      return;
    }

    setPromoteLoading(true);
    setMessage(`🔍 Looking for user with email: ${adminEmail}...`);

    try {
      // Get all users and find by email
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);

      let targetUser = null;
      let targetUserId = null;

      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.email === adminEmail) {
          targetUser = userData;
          targetUserId = doc.id;
        }
      });

      if (targetUser && targetUserId) {
        setMessage('✅ User found! Promoting to admin...');

        // Update user role to admin and approve status
        await updateDoc(doc(db, 'users', targetUserId), {
          role: 'admin',
          status: 'approved',
          updatedAt: new Date()
        });

        setMessage(`✅ Successfully promoted ${adminEmail} to admin!`);
      } else {
        setMessage(`❌ User with email ${adminEmail} not found. Please make sure they have created an account first.`);
      }
    } catch (error) {
      console.error('Error promoting user to admin:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setPromoteLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Admin Account Approval</h1>
        
        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            Your account is currently pending approval. Click the button below to approve yourself as an admin.
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
            <div className="flex">
              <svg className="w-5 h-5 text-blue-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-blue-800">Current Status</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Email: {currentUser?.email}<br/>
                  Status: Pending Approval<br/>
                  Role: Member
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-4 mb-8">
          <button
            onClick={handleApproveAdmin}
            disabled={loading}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Approving...' : 'Approve Admin Account'}
          </button>
        </div>

        {/* Promote Other User Section */}
        <div className="border-t pt-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Promote Another User to Admin</h2>
          <p className="text-gray-600 mb-4">
            Enter the email address of a user who has already created an account to promote them to admin.
          </p>

          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <label htmlFor="adminEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="adminEmail"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                placeholder="Enter email address"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={promoteUserByEmail}
              disabled={promoteLoading || !adminEmail}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              {promoteLoading ? 'Promoting...' : 'Promote to Admin'}
            </button>
          </div>
        </div>

        {message && (
          <div className={`mt-6 p-4 rounded-md ${
            message.includes('✅') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message}
          </div>
        )}

        {message.includes('✅') && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Next Steps:</h3>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>Refresh this page or sign out and sign back in</li>
              <li>You should now see the full admin dashboard</li>
              <li>Go to <a href="/admin/seed-data" className="underline">/admin/seed-data</a> to create sample data</li>
              <li>Test the group admin interface at <a href="/groups/1/admin" className="underline">/groups/1/admin</a></li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminApproval;
